import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { fmtErr } from "@/lib/api";
import { Lock, ShieldCheck, AlertTriangle } from "lucide-react";

export default function Activate() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const nav = useNavigate();
    const [info, setInfo] = useState(null); // { name, email }
    const [invalid, setInvalid] = useState("");
    const [pw, setPw] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) { setInvalid("This activation link is missing its token. Please use the link from your invitation email."); return; }
        api.get(`/auth/activation/${token}`).then((r) => setInfo(r.data))
            .catch((e) => setInvalid(fmtErr(e.response?.data?.detail)));
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (pw.length < 8) return setErr("Password must be at least 8 characters");
        if (pw !== confirm) return setErr("Passwords do not match");
        setBusy(true);
        try {
            await api.post("/auth/activate", { token, password: pw });
            setDone(true);
        } catch (e2) { setErr(fmtErr(e2.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-brand-ink p-4" data-testid="activate-page">
            <div className="bg-white rounded-md shadow-2xl w-[440px] max-w-full p-8">
                <div className="flex items-center gap-2 mb-5">
                    <img src="/qube-logo.svg" alt="Qube" className="w-8 h-8 rounded-sm" />
                    <div className="font-display font-bold">Qube <span className="text-muted-foreground font-medium">Hub</span></div>
                </div>

                {invalid ? (
                    <div className="text-center" data-testid="activate-invalid">
                        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                        <h1 className="font-display font-bold text-xl mb-1">Link not valid</h1>
                        <p className="text-sm text-muted-foreground mb-5">{invalid}</p>
                        <button onClick={() => nav("/login")} className="w-full bg-brand hover:bg-brand-hover text-white font-display font-semibold text-sm py-2.5 rounded-sm">Go to sign in</button>
                    </div>
                ) : done ? (
                    <div className="text-center" data-testid="activate-done">
                        <ShieldCheck className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
                        <h1 className="font-display font-bold text-xl mb-1">Account activated</h1>
                        <p className="text-sm text-muted-foreground mb-5">Your portal access is ready. You can now sign in with your email and new password.</p>
                        <button data-testid="activate-goto-login" onClick={() => nav("/login")} className="w-full bg-brand hover:bg-brand-hover text-white font-display font-semibold text-sm py-2.5 rounded-sm">Go to sign in</button>
                    </div>
                ) : !info ? (
                    <div className="text-sm text-muted-foreground py-6 text-center">Checking your link…</div>
                ) : (
                    <>
                        <h1 className="font-display font-bold text-xl mb-1">Set your password</h1>
                        <p className="text-sm text-muted-foreground mb-5" data-testid="activate-welcome">{info.welcome || `Welcome${info.name ? `, ${info.name}` : ""}. Choose a password to activate your portal access.`} This is for <b>{info.email}</b>.</p>
                        <form onSubmit={submit} className="space-y-3" data-testid="activate-form">
                            {[["New password", pw, setPw, "activate-new"], ["Confirm password", confirm, setConfirm, "activate-confirm"]].map(([label, val, set, tid]) => (
                                <div key={tid}>
                                    <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-display block mb-1.5">{label}</label>
                                    <div className="flex items-center border border-input rounded-sm bg-white focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20">
                                        <Lock className="w-4 h-4 text-muted-foreground mx-3" />
                                        <input type="password" value={val} onChange={(e) => set(e.target.value)} data-testid={tid} required className="flex-1 py-2.5 pr-3 bg-transparent outline-none text-sm" />
                                    </div>
                                </div>
                            ))}
                            {err && <div className="text-xs text-destructive bg-destructive/10 border border-destructive/20 px-3 py-2 rounded-sm" data-testid="activate-error">{err}</div>}
                            <button type="submit" disabled={busy} data-testid="activate-submit" className="w-full bg-brand hover:bg-brand-hover text-white font-display font-semibold text-sm py-2.5 rounded-sm disabled:opacity-50">{busy ? "Activating…" : "Activate account"}</button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
